import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { Field, Input, Select } from '../ui/Field';
import { ChoiceGroup, Toggle } from '../ui/ChoiceGroup';
import { naira } from '../../utils/format';
import type { PropertyKind, RoommatePreferences } from '../../types/housing';
const BUDGETS = [300000, 450000, 600000, 750000, 900000];
const KINDS: PropertyKind[] = ['Shared apartment', 'Hostel', 'Co-living', 'Apartment'];
const SLEEP = ['Early riser', 'Balanced', 'Night owl'] as const;
const TIDINESS = ['Very tidy', 'Tidy', 'Relaxed'] as const;
const NOISE = ['Quiet', 'Moderate', 'Lively'] as const;
const STUDY = ['At home', 'Library', 'Mixed'] as const;
const SMOKING = ['No smoking', 'Smoker friendly'] as const;
const GUESTS = ['Rarely', 'Sometimes', 'Often'] as const;
interface PreferenceFormProps {
  initial: RoommatePreferences;
  submitLabel: string;
  onSubmit: (preferences: RoommatePreferences) => void;
}
export function PreferenceForm({
  initial,
  submitLabel,
  onSubmit
}: PreferenceFormProps) {
  const [values, setValues] = useState<RoommatePreferences>(initial);
  function update<K extends keyof RoommatePreferences>(key: K, value: RoommatePreferences[K]) {
    setValues(current => ({
      ...current,
      [key]: value
    }));
  }
  return <form className="space-y-6" onSubmit={event => {
    event.preventDefault();
    onSubmit(values);
  }} data-id="element-2006" data-mp="1dul3yokhuwjq">
      <div className="grid gap-4 sm:grid-cols-2" data-id="element-2007" data-mp="1mtonturhjfza">
        <Field label="Budget per year" htmlFor="pref-budget" data-id="element-2008" data-mp="1ms59wataxh0o">
          <Select id="pref-budget" value={String(values.budgetMax)} onChange={event => update('budgetMax', Number(event.target.value))} data-id="element-2009" data-mp="1hvn73ghj7sqz">
            {BUDGETS.map(budget => <option key={budget} value={budget} data-id="element-2010" data-mp="1rl6tgw4ge4ql">
                Up to {naira(budget)}
              </option>)}
          </Select>
        </Field>
        <Field label="Move-in date" htmlFor="pref-movein" data-id="element-2011" data-mp="21je6j3vmg2nb">
          <Input id="pref-movein" type="date" value={values.moveIn} onChange={event => update('moveIn', event.target.value)} data-id="element-2012" data-mp="21grp8cixi8rw" />
        </Field>
        <Field label="Accommodation type" htmlFor="pref-kind" data-id="element-2013" data-mp="2dj1f0sdjj7be">
          <Select id="pref-kind" value={values.kind} onChange={event => update('kind', event.target.value as PropertyKind)} data-id="element-2014" data-mp="2ijgp9b2izyp5">
            {KINDS.map(kind => <option key={kind} value={kind} data-id="element-2015" data-mp="2rc0q8i6aifd5">
                {kind}
              </option>)}
          </Select>
        </Field>
        <Field label="How many people in total" htmlFor="pref-count" data-id="element-2016" data-mp="31aq8wd60g3ug">
          <Select id="pref-count" value={String(values.roommateCount)} onChange={event => update('roommateCount', Number(event.target.value))} data-id="element-2017" data-mp="31bhyn7cxf7ct">
            <option value="2" data-id="element-2018" data-mp="16yjnymnbe8oy">Two of us</option>
            <option value="3" data-id="element-2019" data-mp="1bva1p3bncs77">Three of us</option>
            <option value="4" data-id="element-2020" data-mp="1bw20en0jj61p">Four or more</option>
          </Select>
        </Field>
      </div>

      <div className="grid gap-5 border-t border-line pt-5 sm:grid-cols-2" data-id="element-2021" data-mp="3p7ctlwh705ie">
        <ChoiceGroup label="Sleep schedule" options={SLEEP} value={values.sleep} onChange={value => update('sleep', value)} size="sm" data-id="element-2022" data-mp="3kea6o76x7ub6" />
        <ChoiceGroup label="Cleanliness" options={TIDINESS} value={values.tidiness} onChange={value => update('tidiness', value)} size="sm" data-id="element-2023" data-mp="3bfpvdr3owmwz" />
        <ChoiceGroup label="Noise at home" options={NOISE} value={values.noise} onChange={value => update('noise', value)} size="sm" data-id="element-2024" data-mp="7yzj4uhildmu" />
        <ChoiceGroup label="Where you study" options={STUDY} value={values.study} onChange={value => update('study', value)} size="sm" data-id="element-2025" data-mp="3u43a2kwwt34c" />
        <ChoiceGroup label="Smoking" options={SMOKING} value={values.smoking} onChange={value => update('smoking', value)} size="sm" data-id="element-2026" data-mp="2ioy3h5tnqkfg" />
        <ChoiceGroup label="Guests" options={GUESTS} value={values.guests} onChange={value => update('guests', value)} size="sm" data-id="element-2027" data-mp="f89r43x7627u" />
      </div>

      <div className="border-t border-line pt-2" data-id="element-2028" data-mp="6gra5qsffidp">
        <Toggle checked={values.sharedShopping} onChange={value => update('sharedShopping', value)} label="Happy to share food shopping and bills" description="Used for matching only. It is never shown as a commitment." data-id="element-2029" data-mp="6dbqeant5l5y" />
      </div>

      <div className="flex justify-end border-t border-line pt-5" data-id="element-2030" data-mp="k6ulvntojtow">
        <Button type="submit" variant="primary" data-id="element-2031" data-mp="p54i81r550c3">
          {submitLabel}
        </Button>
      </div>
    </form>;
}